/**
 * Travel Wallet: what Elio reads from a document, and the checks against the traveller's trip.
 * Shared by the Worker (which asks the model and checks the result) and the app (which shows it
 * for the traveller to confirm). Nothing here writes data.
 */
import { z } from 'zod';
import { itemSchema, payloadSchemas } from './assistant';

/** Model output is forgiving: blanks and over-long text become null instead of failing the whole reading. */
const loose = (n: number) => z.string().nullish().transform((v) => { const t = (v ?? '').trim(); return t ? t.slice(0, n) : null; });
const looseDate = z.string().nullish().transform((v) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null));

export const DOC_TYPES = ['ticket', 'boarding_pass', 'hotel', 'rental', 'activity', 'insurance', 'receipt', 'identity', 'visa', 'other'] as const;
export type DocType = (typeof DOC_TYPES)[number];

/** What the model returns (lenient), validated and cleaned by the Worker. */
export const modelReadingSchema = z.object({
  readable: z.boolean(),
  category: z.enum(DOC_TYPES).catch('other'),
  title: loose(120),
  subtitle: loose(160),
  provider: loose(120),
  reference: loose(80),
  starts_on: looseDate,
  ends_on: looseDate,
  expires_on: looseDate,
  trip_id: loose(64),
  trip_reason: loose(300),
  key_facts: z.array(z.object({ label: loose(40), value: loose(200) })).max(12).catch([]).default([]),
  booking: z.object({ item: itemSchema, booking: payloadSchemas.add_booking.shape.booking }).nullish().catch(null).transform((v) => v ?? null),
  expense: payloadSchemas.add_expense.omit({ source: true, split: true }).nullish().catch(null).transform((v) => v ?? null),
  warnings: z.array(z.string()).max(8).catch([]).default([]),
  uncertain_fields: z.array(z.string()).max(20).catch([]).default([]),
  confidence: z.enum(['high', 'medium', 'low']).catch('low'),
});
export type ModelReading = z.output<typeof modelReadingSchema>;

export interface WalletConflict { level: 'warning' | 'info'; text: string }

/** What the app receives and shows for confirmation. */
export interface DocumentReading {
  readable: boolean;
  category: DocType;
  title: string | null;
  subtitle: string | null;
  provider: string | null;
  reference: string | null;
  starts_on: string | null;
  ends_on: string | null;
  expires_on: string | null;
  trip_id: string | null;
  trip_reason: string | null;
  key_facts: { label: string; value: string }[];
  /** A booking ready to add to the trip (same shape Elio uses in conversation). */
  booking: z.input<typeof payloadSchemas.add_booking> | null;
  /** An expense ready to add (receipts). */
  expense: z.input<typeof payloadSchemas.add_expense> | null;
  warnings: string[];
  conflicts: WalletConflict[];
  confidence: 'high' | 'medium' | 'low';
}

export const readDocumentRequestSchema = z.object({
  documentId: z.string().uuid().nullish().transform((v) => v ?? null),
  attachment: z.object({
    name: z.string().min(1).max(120),
    kind: z.enum(['image', 'pdf']),
    dataUrl: z.string().max(12 * 1024 * 1024).regex(/^data:(image\/(jpeg|png|webp)|application\/pdf);base64,/, 'Unsupported file'),
  }),
  client: z.object({ timeZone: z.string().max(64).nullish() }).default({}),
});
export type ReadDocumentRequest = z.input<typeof readDocumentRequestSchema>;

// ---------------------------------------------------------------- checks against the trip

export interface TripFacts {
  id: string; title: string; start_date: string | null; end_date: string | null;
}
export interface EventFacts {
  id: string; title: string; category: string; status: string;
  start_date: string | null; end_date: string | null; service_number: string | null;
  reference: string | null;
}

const fmt = (d: string) => new Date(`${d}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const overlaps = (a1: string, a2: string, b1: string, b2: string) => a1 < b2 && b1 < a2; // nights: check-out day is free
const TRANSPORT = new Set(['flight', 'rail', 'coach', 'ferry', 'cruise', 'road', 'transfer']);

/**
 * Plain, explainable checks: duplicate of something already saved, dates outside the trip,
 * a second hotel for the same nights, a clashing journey. Also finds a planned item this booking replaces.
 */
export function checkAgainstTrip(reading: { reference: string | null; starts_on: string | null; ends_on: string | null; category: DocType; booking: { item: { category: string; start_date?: string | null; end_date?: string | null; service_number?: string | null; title: string } } | null },
  trip: TripFacts | null, events: EventFacts[]): { conflicts: WalletConflict[]; replacesEventId: string | null } {
  const conflicts: WalletConflict[] = [];
  let replacesEventId: string | null = null;
  if (!trip) return { conflicts, replacesEventId };
  const start = reading.booking?.item.start_date ?? reading.starts_on;
  const end = reading.booking?.item.end_date ?? reading.ends_on ?? start;
  const ref = reading.reference?.replace(/\s+/g, '').toUpperCase() ?? null;

  const dup = ref ? events.find((e) => e.reference && e.reference.replace(/\s+/g, '').toUpperCase() === ref) : undefined;
  if (dup) {
    conflicts.push({ level: 'warning', text: `Booking reference ${reading.reference} is already in ${trip.title} (“${dup.title}”). Adding it again would duplicate it.` });
    return { conflicts, replacesEventId };
  }

  if (start && trip.start_date && trip.end_date && (start < trip.start_date || (end ?? start) > trip.end_date)) {
    conflicts.push({ level: 'warning', text: `These dates (${fmt(start)}${end && end !== start ? `–${fmt(end)}` : ''}) fall outside ${trip.title} (${fmt(trip.start_date)}–${fmt(trip.end_date)}).` });
  }

  const cat = reading.booking?.item.category ?? (reading.category === 'hotel' ? 'accommodation' : null);
  if (cat === 'accommodation' && start && end && end > start) {
    const clash = events.find((e) => e.category === 'accommodation' && e.status === 'confirmed' && e.start_date && e.end_date && overlaps(start, end, e.start_date, e.end_date));
    if (clash) conflicts.push({ level: 'warning', text: `You already have “${clash.title}” booked for some of these nights (${fmt(clash.start_date!)}–${fmt(clash.end_date!)}).` });
  }
  const svc = reading.booking?.item.service_number?.replace(/\s+/g, '').toUpperCase();
  if (cat && TRANSPORT.has(cat) && start) {
    const same = events.find((e) => e.status === 'confirmed' && e.start_date === start && svc && e.service_number?.replace(/\s+/g, '').toUpperCase() === svc);
    if (same) conflicts.push({ level: 'warning', text: `“${same.title}” on ${fmt(start)} looks like the same journey, already booked.` });
    else {
      const other = events.find((e) => e.status === 'confirmed' && TRANSPORT.has(e.category) && e.start_date === start);
      if (other) conflicts.push({ level: 'info', text: `You also have “${other.title}” booked on ${fmt(start)}. Check the two journeys fit together.` });
    }
  }

  // A planned or suggested item of the same kind on the same day becomes this booking.
  if (cat && start) {
    const planned = events.find((e) => (e.status === 'planned' || e.status === 'suggested') && e.category === cat && e.start_date === start);
    if (planned) {
      replacesEventId = planned.id;
      conflicts.push({ level: 'info', text: `Replaces your planned “${planned.title}” with the confirmed booking.` });
    }
  }
  return { conflicts, replacesEventId };
}

export const DOC_TYPE_LABEL: Record<DocType, string> = {
  ticket: 'Ticket', boarding_pass: 'Boarding pass', hotel: 'Hotel booking', rental: 'Car rental', activity: 'Activity or tour',
  insurance: 'Travel insurance', receipt: 'Receipt', identity: 'Passport or ID card', visa: 'Visa', other: 'Other',
};

/** A first guess at the document type from its file name, so nobody has to choose before uploading. */
export function guessType(name: string): DocType {
  const n = name.toLowerCase();
  if (/boarding/.test(n)) return 'boarding_pass';
  if (/\b(visa|evisa|esta|etias|eta)\b/.test(n)) return 'visa';
  if (/insur|policy/.test(n)) return 'insurance';
  if (/receipt|invoice|bill|rekening|facture|quittung/.test(n)) return 'receipt';
  if (/hotel|booking|reservation|airbnb|apartment|hostel|stay|accommodation/.test(n)) return 'hotel';
  if (/\bcar\b|rental|hertz|avis|sixt|europcar/.test(n)) return 'rental';
  if (/museum|tour|tickets?.*(entry|admission)|excursion|activity/.test(n)) return 'activity';
  if (/ticket|train|rail|eurostar|flight|e-?ticket|itinerary|pnr|bus|ferry/.test(n)) return 'ticket';
  return 'other';
}
